import InfiniteScroll from 'components/InfiniteScroll/InfiniteScroll';
import Loader from 'components/Loader/Loader';
import NoFeedback from 'public/icons/icn-no-feedback.svg';
import { RefObject, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ShopFeedbackService from '../../services/shop-feedback.service';
import { ShopType } from '../../types/shop.types';
import { ShopFeedback as ShopFeedbackType, ShopFeedbackPublic } from '../../types/shop-feedback.types';
import { UserType } from '../../types/user.types';
import { ShopFeedbackTabs } from '../ShopFeedbackTab/ShopFeedbackTab';
import { ShopFeedback, ShopFeedbackActionType } from './ShopFeedback';

interface ShopFeedbacksContainerProps {
  isOpen: boolean;
  setOpen: (open?: boolean) => void;
  modalContentRef: RefObject<HTMLDivElement>;
  feedbackReceiver: Partial<UserType> & { shop: Partial<ShopType> };
  filter?: ShopFeedbackTabs;
  refresh?: () => void;
  /** Automatic reviews already reported as shown in this shop visit (owned by the shop page). */
  trackedAutomaticReviewIds: Set<number>;
}

type ShopFeedbackItem = ShopFeedbackType & {
  fromUser: (Partial<UserType> & { shop: Partial<ShopType> }) | null;
  canReply: boolean;
};

const SHOP_FEEDBACKS_PER_PAGE_LIMIT = 25;

const shopFeedbackService = new ShopFeedbackService();

export const ShopFeedbacks = ({
  isOpen,
  setOpen,
  feedbackReceiver,
  modalContentRef,
  filter,
  refresh,
  trackedAutomaticReviewIds,
}: ShopFeedbacksContainerProps) => {
  const [hasMoreShopFeedbacks, setHasMoreShopFeedbacks] = useState(true);
  const [shopFeedbacks, setShopFeedbacks] = useState<ShopFeedbackItem[]>([]);
  const [isLoading, setLoading] = useState(true);

  const shopId = feedbackReceiver?.shop?.id;

  const loadMoreShopFeedbacks = async (page: number) => {
    setLoading(true);

    const { list: userShopFeedbacks, total } = await shopFeedbackService.getShopFeedbacks(shopId, {
      offset: page * SHOP_FEEDBACKS_PER_PAGE_LIMIT,
      limit: SHOP_FEEDBACKS_PER_PAGE_LIMIT,
      filter,
    });

    // Merge on current state, not the render-time closure: page 0 and page 1 can be in flight
    // at the same time, and a closure-based concat would overwrite one page with the other.
    setShopFeedbacks((currentShopFeedbacks) => {
      const combinedShopFeedbacks = currentShopFeedbacks.concat(userShopFeedbacks || []);

      setHasMoreShopFeedbacks(!!userShopFeedbacks?.length && total > combinedShopFeedbacks.length);

      return combinedShopFeedbacks;
    });

    setLoading(false);
  };

  const onUpdateFeedback = (updatedFeedback: ShopFeedbackPublic, action: ShopFeedbackActionType, i: number) => {
    setShopFeedbacks((currentShopFeedbacks) => {
      const feedbacksClone = [...currentShopFeedbacks];

      // If deleted, after previous state set, do another one that resets all.
      if (action === ShopFeedbackActionType.REVIEW_DELETED) {
        feedbacksClone.splice(i, 1);
      } else {
        feedbacksClone[i] = {
          ...feedbacksClone[i],
          ...updatedFeedback,
        };
      }

      return feedbacksClone;
    });

    refresh?.();
  };

  useEffect(() => {
    setShopFeedbacks([]);
    setHasMoreShopFeedbacks(true);
  }, [shopId]);

  return (
    <>
      <div className="shop-feedback-content pt-1">
        {/* Shop feedbacks */}
        <InfiniteScroll
          batchSize={SHOP_FEEDBACKS_PER_PAGE_LIMIT}
          dataLength={shopFeedbacks.length}
          loadMore={loadMoreShopFeedbacks}
          hasMore={hasMoreShopFeedbacks}
          loader={<Loader my={2} />}
          parentRef={modalContentRef}
          resetDependencies={[shopId, isOpen]}
        >
          {!shopFeedbacks.length && !isLoading ? <NoShopFeedbacks /> : null}
          {shopFeedbacks.map((feedback, i) => (
            <ShopFeedback
              key={'shop-feedback-' + feedback.id}
              feedback={feedback}
              feedbackSender={feedback.fromUser}
              feedbackReceiver={feedbackReceiver}
              trackedAutomaticReviewIds={trackedAutomaticReviewIds}
              onClickSender={() => setOpen(false)}
              onRefresh={(updatedFeedback, action) => onUpdateFeedback(updatedFeedback, action, i)}
            />
          ))}
        </InfiniteScroll>
      </div>
    </>
  );
};

const NoShopFeedbacks = () => {
  const { t } = useTranslation();

  return (
    <div className="no-shop-feedback">
      <NoFeedback />
      <div className="no-shop-feedback-title">{t('components.feedback.no-feedback.title')}</div>
      <div className="no-shop-feedback-body">{t('components.feedback.no-feedback.body')}</div>
    </div>
  );
};
